/**
 * UnifiedParsingModal - Modular version
 *
 * This is the new modular version of the UnifiedParsingModal component.
 * It uses separate step components for better maintainability.
 *
 * Step structure:
 * 0. Method Selection (Yandex Parsing / Manual URL Input)
 * 1. Configuration & Start (for Yandex) / URL Input (for Manual)
 * 2. Parsing Progress (skipped for Manual)
 * 3. Parsing Results
 * 4. AI Analysis
 * 5. Manual Editing
 * 6. Upload Confirmation
 * 7. Upload Results
 */

import { useEffect, useState, useCallback } from 'react'
import {
  Modal,
  Steps,
  Space,
  Button,
  Divider,
  Form,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  LoadingOutlined,
  RocketOutlined,
  SearchOutlined,
  ExperimentOutlined,
  EditOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  ToolOutlined,
  FormOutlined,
  GlobalOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { UnifiedParsingModalProps, DomainWithAnalysis } from './types'
import { STEP_NAMES, STEP_INDEX } from './constants'
import { isPhoneNumber, extractDomainFromUrl } from './utils'
import { normalizePhone } from './utils/phoneUtils'

// Import step components
import {
  MethodSelectionStep,
  ManualInputStep,
  ParsingStep,
  ConfigStep,
  ResultsStep,
  AIAnalysisStep,
  EditingStep,
  ConfirmStep,
  UploadResultsStep,
} from './steps'

// Import hooks
import { useParsingWorkflow } from './hooks'
import api from '@/services/api'

// Error boundary
import { ErrorBoundary } from './ErrorBoundary'

const { Step } = Steps

type StepName = typeof STEP_NAMES[number]

export default function UnifiedParsingModalNew({
  visible,
  projectId,
  projectCrmId,
  projectName,
  credentialsId,
  taskId: initialTaskId,
  onClose,
}: UnifiedParsingModalProps) {
  const [form] = Form.useForm()

  // Use the workflow hook for state management
  const workflow = useParsingWorkflow({
    projectId,
    projectCrmId,
    projectName,
    credentialsId,
    initialTaskId,
    onClose,
    form,
  })

  // Initialize modal on open
  useEffect(() => {
    if (visible) {
      workflow.initializeModal()
    } else {
      workflow.resetState()
    }
  }, [visible, projectId, projectCrmId, initialTaskId])

  // Reanalyze: kicks new batch with same URLs but current ai-config version
  const [reanalyzing, setReanalyzing] = useState(false)
  const handleReanalyze = useCallback(async () => {
    const batchId = workflow.activeBatchId
    if (!batchId) {
      message.warning('Активный batch не найден')
      return
    }
    setReanalyzing(true)
    try {
      const result = await api.reanalyzeBatch(batchId)
      message.success(`Пересчёт запущен (batch #${result.batch_id})`)
      workflow.setActiveBatchId(result.batch_id)
      workflow.setAnalyzing(true)
      if (typeof workflow.resumeAnalysisPoll === 'function') {
        workflow.resumeAnalysisPoll(result.batch_id)
      }
    } catch (e) {
      console.error('reanalyzeBatch failed:', e)
      message.error('Не удалось запустить пересчёт')
    } finally {
      setReanalyzing(false)
    }
  }, [workflow])

  // Auto-select results tab
  useEffect(() => {
    if (workflow.currentStep === STEP_INDEX.results && workflow.parsingResult) {
      const domainsCount = workflow.domains?.length || 0
      const urlsCount = workflow.parsingResult.urls?.length || 0
      const phonesCount = workflow.parsingResult.phones?.length || 0

      if (domainsCount > 0) {
        workflow.setResultsTab('domains')
      } else if (urlsCount > 0) {
        workflow.setResultsTab('urls')
      } else if (phonesCount > 0) {
        workflow.setResultsTab('phones')
      }
    }
  }, [workflow.currentStep, workflow.parsingResult, workflow.domains])

  // Auto-select all phones
  useEffect(() => {
    if (workflow.parsingResult?.phones && workflow.parsingResult.phones.length > 0) {
      const allPhones = workflow.parsingResult.phones
        .map((p: any) => normalizePhone(p.phone_number))
        .filter(Boolean) as string[]
      workflow.setSelectedPhones(new Set(allPhones))
    }
  }, [workflow.parsingResult?.phones])

  // Handle step navigation
  const handleNext = async () => {
    const step = STEP_NAMES[workflow.currentStep] as StepName

    if (step === 'method') {
      if (!workflow.inputMethod) {
        message.warning('Выберите метод загрузки источников')
        return
      }
      workflow.setCurrentStep(workflow.currentStep + 1)
      return
    }

    if (step === 'config') {
      if (workflow.inputMethod === 'manual') {
        await handleProcessManualUrls()
        return
      }

      if (projectId) {
        await workflow.handleStartParsing()
      } else {
        form.submit()
      }
    } else if (step === 'results') {
      if (workflow.domains.length === 0) {
        message.warning('Невозможно продолжить: не найдено ни одного домена.')
        return
      }
      workflow.loadAIConfig()
      const nextStep = workflow.currentStep + 1
      workflow.setCurrentStep(nextStep)
      // * Persist manual workflow state
      workflow.persistManualWorkflowState(nextStep)
    } else if (step === 'ai_analysis') {
      if (workflow.domains.length === 0) {
        message.warning('Нет доменов для анализа')
        return
      }
      const analyzedCount = workflow.domains.filter((d) => d.ai_score !== undefined).length
      if (analyzedCount === 0 && !workflow.analyzing) {
        message.info('Запустите AI анализ или пропустите этот шаг')
        return
      }
      const nextStep = workflow.currentStep + 1
      workflow.setCurrentStep(nextStep)
      // * Persist manual workflow state
      workflow.persistManualWorkflowState(nextStep)
    } else if (step === 'editing') {
      const selectedCount = workflow.domains.filter((d) => d.selected).length
      if (selectedCount === 0) {
        message.warning('Выберите хотя бы один домен для загрузки')
        return
      }
      await workflow.saveSourceSelections()
      const nextStep = workflow.currentStep + 1
      workflow.setCurrentStep(nextStep)
      // * Persist manual workflow state
      workflow.persistManualWorkflowState(nextStep)
    } else {
      const nextStep = workflow.currentStep + 1
      workflow.setCurrentStep(nextStep)
      // * Persist manual workflow state
      workflow.persistManualWorkflowState(nextStep)
    }
  }

  const handlePrev = () => {
    if (workflow.currentStep > 0) {
      if (workflow.currentStep === STEP_INDEX.results && workflow.inputMethod === 'manual') {
        workflow.setCurrentStep(STEP_INDEX.config)
      } else {
        workflow.setCurrentStep(workflow.currentStep - 1)
      }
    }
  }

  // Process manual URL input
  const handleProcessManualUrls = async () => {
    if (!workflow.manualUrlsText.trim()) {
      message.warning('Введите хотя бы одну ссылку')
      return
    }

    workflow.setProcessingManualUrls(true)

    try {
      const lines = workflow.manualUrlsText
        .split(/[\n\r]+/)
        .map(line => line.trim())
        .filter(line => line.length > 0)

      const domainSet = new Set<string>()
      const urlsList: any[] = []
      const phoneNumbers: string[] = []

      lines.forEach(line => {
        if (isPhoneNumber(line)) {
          phoneNumbers.push(line)
          return
        }

        const domain = extractDomainFromUrl(line)
        if (!domain) return

        let fullUrl = line
        if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
          fullUrl = `https://${fullUrl}`
        }

        try {
          const url = new URL(fullUrl)
          const hasPath = url.pathname !== '/' && url.pathname !== ''

          if (hasPath) {
            urlsList.push({
              full_url: fullUrl,
              domain: domain,
              path: url.pathname,
            })
          } else {
            domainSet.add(domain)
          }
        } catch {
          domainSet.add(domain)
        }
      })

      const uniqueDomains = Array.from(domainSet)

      const formattedDomains: DomainWithAnalysis[] = uniqueDomains.map((domain) => ({
        host: domain,
        full_url: `https://${domain}/`,
        source_type: 'domain' as const,
        total_clicks: 0,
        queries_count: 0,
        average_position: 0,
        popularity: 'manual',
        selected: true,
      }))

      const formattedPages: DomainWithAnalysis[] = urlsList.map((urlObj) => ({
        host: urlObj.domain,
        full_url: urlObj.full_url,
        source_type: 'url' as const,
        total_clicks: 0,
        queries_count: 0,
        average_position: 0,
        popularity: 'manual',
        selected: true,
      }))

      const allForAnalysis = [...formattedDomains, ...formattedPages]

      const manualTaskId = `manual_${Date.now()}`

      workflow.setTaskId(manualTaskId)
      workflow.setDomains(allForAnalysis)
      workflow.setParsingResult({
        success: true,
        task_id: manualTaskId,
        domains: formattedDomains as any,
        pages: formattedPages as any,
        urls: urlsList,
        phones: phoneNumbers.map(p => ({ phone_number: p })),
        domains_count: formattedDomains.length,
        pages_count: formattedPages.length,
        urls_count: urlsList.length,
        phones_count: phoneNumbers.length,
        input_method: 'manual',
      })

      message.success(`✅ Обработано: ${uniqueDomains.length} доменов, ${urlsList.length} ссылок`)
      workflow.setCurrentStep(STEP_INDEX.results)
      // * Persist manual workflow state immediately after processing
      workflow.persistManualWorkflowState(STEP_INDEX.results)
    } catch (error: any) {
      message.error('Ошибка обработки ссылок: ' + (error.message || 'Неизвестная ошибка'))
    } finally {
      workflow.setProcessingManualUrls(false)
    }
  }

  const handleRetry = () => {
    workflow.setParsingFailed(false)
    workflow.setCurrentStep(STEP_INDEX.method)
    if (projectId && projectCrmId) {
      // Restore project context for retry
      workflow.setSelectedCrmId(projectCrmId)
      workflow.loadQueriesRegions(projectId)
    }
  }

  // Render step content
  const renderStepContent = () => {
    const step = STEP_NAMES[workflow.currentStep] as StepName

    switch (step) {
      case 'method':
        return (
          <MethodSelectionStep
            projectName={projectName}
            inputMethod={workflow.inputMethod}
            onSelectMethod={workflow.setInputMethod}
          />
        )

      case 'config':
        if (workflow.inputMethod === 'manual') {
          return (
            <ManualInputStep
              projectName={projectName}
              manualUrlsText={workflow.manualUrlsText}
              onTextChange={workflow.setManualUrlsText}
              subsource={workflow.manualSubsource}
              onSubsourceChange={workflow.setManualSubsource}
              processingManualUrls={workflow.processingManualUrls}
            />
          )
        }
        return (
          <ConfigStep
            projectId={projectId}
            projectName={projectName}
            form={form}
            projects={workflow.projects}
            loadingProjects={workflow.loadingProjects}
            onLoadQueriesRegions={workflow.loadQueriesRegions}
            onStartParsing={workflow.handleStartParsing}
            queriesRegions={workflow.queriesRegions}
            loadingQueriesRegions={workflow.loadingQueriesRegions}
            resolvedRegions={workflow.resolvedRegions}
            selectedQueries={workflow.selectedQueries}
            setSelectedQueries={workflow.setSelectedQueries}
            selectedRegionCodes={workflow.selectedRegionCodes}
            setSelectedRegionCodes={workflow.setSelectedRegionCodes}
            downloadAll={workflow.downloadAll}
            setDownloadAll={workflow.setDownloadAll}
            minClicks={workflow.minClicks}
            setMinClicks={workflow.setMinClicks}
            topQueries={workflow.topQueries}
            setTopQueries={workflow.setTopQueries}
          />
        )

      case 'parsing':
        return (
          <ParsingStep
            projectName={projectName}
            projectCrmId={projectCrmId}
            progress={workflow.progress}
            statusMessage={workflow.statusMessage}
            parsingFailed={workflow.parsingFailed}
            onRetry={handleRetry}
            onClose={onClose}
          />
        )

      case 'results':
        return (
          <ResultsStep
            inputMethod={workflow.inputMethod}
            parsingResult={workflow.parsingResult}
            domains={workflow.domains}
            resultsTab={workflow.resultsTab}
            onResultsTabChange={workflow.setResultsTab}
            onRetry={handleRetry}
            analyzing={workflow.analyzing}
          />
        )

      case 'ai_analysis':
        return (
          <AIAnalysisStep
            domains={workflow.domains}
            aiConfig={workflow.aiConfig}
            loadingAIConfig={workflow.loadingAIConfig}
            analyzing={workflow.analyzing}
            batchProgress={workflow.batchProgress}
            queueInfo={workflow.queueInfo}
            aiFilter={workflow.aiFilter}
            onAiFilterChange={workflow.setAiFilter}
            aiSearchText={workflow.aiSearchText}
            onAiSearchTextChange={workflow.setAiSearchText}
            onStartAnalysis={workflow.handleAnalyze}
            currentBatchConfigVersion={workflow.serverWorkflow?.active_batch?.config_version ?? null}
            currentProjectConfigVersion={workflow.serverWorkflow?.current_config_version ?? workflow.aiConfig?.config_version ?? null}
            onReanalyze={handleReanalyze}
            reanalyzing={reanalyzing}
          />
        )

      case 'editing':
        return (
          <EditingStep
            projectName={projectName}
            inputMethod={workflow.inputMethod}
            domains={workflow.domains}
            parsingResult={workflow.parsingResult}
            selectedPhones={workflow.selectedPhones}
            searchText={workflow.searchText}
            onSearchTextChange={workflow.setSearchText}
            onToggleDomainSelection={workflow.toggleDomainSelection}
            onRemoveDomain={workflow.removeDomain}
            onSetDomains={workflow.setDomains}
            onSetSelectedPhones={workflow.setSelectedPhones}
          />
        )

      case 'confirm':
        return (
          <ConfirmStep
            projectName={projectName}
            projectCrmId={projectCrmId}
            domains={workflow.domains}
            selectedPhones={workflow.selectedPhones}
            uploading={workflow.uploading}
            uploadProgress={workflow.uploadProgress}
            uploadResults={workflow.uploadResults}
            showAllDomains={workflow.showAllDomains}
            onShowAllDomainsChange={workflow.setShowAllDomains}
            onUpload={workflow.handleUpload}
          />
        )

      case 'upload_results':
        return (
          <UploadResultsStep
            uploadResults={workflow.uploadResults}
            localDbResults={workflow.localDbResults}
            onClose={() => {
              if (projectId) {
                workflow.clearTask(projectId)
                workflow.clearManualWorkflow(projectId)
              }
              onClose()
            }}
          />
        )

      default:
        return null
    }
  }

  // Check if "Start Over" button should be shown
  const canShowStartOver = () => {
    const step = STEP_NAMES[workflow.currentStep]
    // Show on all steps except first (method) and last (upload_results)
    // Also hide during active processes
    if (step === 'method') return false
    if (step === 'upload_results') return false
    if (step === 'parsing' && !workflow.parsingFailed) return false
    if (step === 'confirm' && workflow.uploading) return false
    return true
  }

  // Render navigation buttons
  const renderNavigationButtons = () => {
    const step = STEP_NAMES[workflow.currentStep]
    if (!step) return null

    if (step === 'upload_results') return null
    if (step === 'parsing' && !workflow.parsingFailed) return null
    if (step === 'confirm' && workflow.uploading) return null
    if (step === 'config' && workflow.inputMethod === 'manual' && workflow.processingManualUrls) return null

    const canGoNext = (() => {
      if (step === 'method') return workflow.inputMethod !== null
      if (step === 'config') {
        if (workflow.inputMethod === 'manual') {
          const lines = workflow.manualUrlsText.split(/[\n\r]+/).filter(l => l.trim())
          return lines.length > 0 && !workflow.processingManualUrls
        }
        return !workflow.loadingQueriesRegions &&
          workflow.selectedQueries.length > 0
      }
      if (step === 'parsing') return false
      if (step === 'ai_analysis') {
        const analyzedCount = workflow.domains.filter((d) => d.ai_score !== undefined).length
        return analyzedCount > 0 || workflow.analyzing
      }
      return true
    })()

    const getButtonText = () => {
      if (step === 'method') return 'Продолжить'
      if (step === 'config') {
        if (workflow.inputMethod === 'manual') {
          return workflow.processingManualUrls ? 'Обработка...' : 'Обработать ссылки'
        }
        return 'Запустить парсинг'
      }
      return 'Далее'
    }

    return (
      <Space>
        {workflow.currentStep > 0 && step !== 'parsing' && (
          <Button onClick={handlePrev}>Назад</Button>
        )}

        {step !== 'confirm' && (
          <Button
            type="primary"
            onClick={handleNext}
            disabled={!canGoNext}
            loading={step === 'config' && workflow.inputMethod === 'manual' && workflow.processingManualUrls}
          >
            {getButtonText()}
          </Button>
        )}
      </Space>
    )
  }

  // Build steps configuration
  const buildSteps = () => {
    const isManualMethod = workflow.inputMethod === 'manual'
    const { currentStep, progress, parsingFailed, analyzing, aiAnalysisSkipped, uploading, domains, uploadResults, selectedCount } = workflow

    return [
      {
        title: 'Метод',
        icon: currentStep > 0 ? <CheckCircleOutlined /> : <RocketOutlined />,
        description: workflow.inputMethod === 'yandex' ? 'Yandex' : workflow.inputMethod === 'manual' ? 'Ручной' : 'Выбор'
      },
      {
        title: isManualMethod ? 'Ввод ссылок' : 'Конфигурация',
        icon: currentStep > 1 ? <CheckCircleOutlined /> : isManualMethod ? <FormOutlined /> : <ToolOutlined />,
        description: currentStep > 1 ? 'Готово' : projectName ? 'Готов' : 'Настройка'
      },
      {
        title: isManualMethod ? 'Обработка' : 'Парсинг',
        icon: currentStep === 2 && !parsingFailed && !isManualMethod
          ? <LoadingOutlined spin />
          : currentStep > 2 ? <CheckCircleOutlined /> : <SearchOutlined />,
        description: currentStep === 2 && !isManualMethod
          ? `${Math.round(progress)}%`
          : currentStep > 2 ? 'Завершен' : isManualMethod ? 'Пропущен' : 'Ожидание'
      },
      {
        title: 'Результаты',
        icon: currentStep > 3 ? <CheckCircleOutlined /> : <FileTextOutlined />,
        description: domains.length > 0 ? `${domains.length} доменов` : 'Ожидание'
      },
      {
        title: 'AI Анализ',
        icon: analyzing ? <LoadingOutlined spin /> : currentStep > 5 ? <CheckCircleOutlined /> : <ExperimentOutlined />,
        description: analyzing ? 'Анализ...' : aiAnalysisSkipped ? 'Пропущен' :
          domains.filter((d) => d.ai_score !== undefined).length > 0 ? 'Завершен' : 'Ожидание'
      },
      {
        title: 'Редактирование',
        icon: currentStep > 6 ? <CheckCircleOutlined /> : <EditOutlined />,
        description: selectedCount > 0 ? `Выбрано: ${selectedCount}` : 'Ожидание'
      },
      {
        title: 'Загрузка',
        icon: uploading ? <LoadingOutlined spin /> : currentStep > 7 ? <CheckCircleOutlined /> : <CloudUploadOutlined />,
        description: uploading ? 'Загрузка...' : currentStep > 7 ? 'Завершена' : 'Ожидание'
      },
      {
        title: 'Готово',
        icon: currentStep === 8 ? <CheckCircleOutlined /> : <CheckCircleOutlined />,
        description: uploadResults.length > 0 ? `${uploadResults.filter(r => r.success).length} загружено` : 'Ожидание'
      },
    ]
  }

  const steps = buildSteps()

  // Modal title
  const getModalTitle = () => {
    if (workflow.inputMethod === 'manual') {
      return (
        <Space>
          <FormOutlined style={{ color: '#52c41a' }} />
          <span>Ручная загрузка источников</span>
        </Space>
      )
    }
    if (workflow.inputMethod === 'yandex') {
      return (
        <Space>
          <GlobalOutlined style={{ color: '#667eea' }} />
          <span>Yandex Парсинг</span>
        </Space>
      )
    }
    return (
      <Space>
        <RocketOutlined />
        <span>Загрузка источников</span>
      </Space>
    )
  }

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnClose
      maskClosable={false}
    >
      <ErrorBoundary onReset={workflow.handleStartOver} onClose={onClose}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Steps current={workflow.currentStep} size="small" style={{ marginBottom: 8 }}>
            {steps.map((step, index) => (
              <Step
                key={index}
                title={step.title}
                description={step.description}
                icon={step.icon}
                status={
                  index < workflow.currentStep ? 'finish' :
                  index === workflow.currentStep ? 'process' :
                  'wait'
                }
              />
            ))}
          </Steps>

          <Divider style={{ margin: '12px 0' }} />

          <div style={{ minHeight: 400 }}>{renderStepContent()}</div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {canShowStartOver() && (
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={workflow.handleStartOver}
                  style={{ color: '#8c8c8c' }}
                >
                  Начать заново
                </Button>
              )}
            </div>
            <div>{renderNavigationButtons()}</div>
          </div>
        </Space>
      </ErrorBoundary>

      {/* Custom styles */}
      <style>
        {`
          .ant-steps-item-tail { padding: 4px 0 8px !important; }
          .ant-steps-item-process .ant-steps-item-icon {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-color: #667eea;
          }
          .ant-steps-item-finish .ant-steps-item-icon { border-color: #52c41a; background: #fff; }
          .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon { color: #52c41a; }
          .ant-steps-item-description { font-size: 12px !important; color: #8c8c8c !important; }
          .ant-steps-item-process .ant-steps-item-description { color: #667eea !important; font-weight: 500; }
        `}
      </style>
    </Modal>
  )
}
